import React from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Link2, Globe, HardDrive, Sparkles, FolderOpen, Paperclip } from 'lucide-react';
import { MediaUploadTab } from './MediaUploadTab';
import { UrlImportTab } from './UrlImportTab';
import { LinkAttachmentTab } from './LinkAttachmentTab';
import { CloudStorageTab } from './CloudStorageTab';
import { AIGenerateTab } from './AIGenerateTab';
import { MediaLibraryTab } from './MediaLibraryTab';
import { SavedMediaTab } from './SavedMediaTab';
import type { SavedMediaItemRef } from './SavedMediaTab';
import type { MediaFile, UrlMedia, LinkPreview, Platform } from './types';

type MediaTab = 'saved' | 'library' | 'upload' | 'url' | 'link' | 'cloud' | 'ai';

interface Props {
  mediaTab: MediaTab;
  setMediaTab: React.Dispatch<React.SetStateAction<MediaTab>>;
  mediaFiles: MediaFile[];
  setMediaFiles: React.Dispatch<React.SetStateAction<MediaFile[]>>;
  urlMediaItems: UrlMedia[];
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
  linkPreview: LinkPreview | null;
  setLinkPreview: React.Dispatch<React.SetStateAction<LinkPreview | null>>;
  selectedPlatforms: string[];
  platforms: Platform[];
  onEditSavedMedia?: (item: SavedMediaItemRef) => void;
}

export const MediaSection: React.FC<Props> = ({
  mediaTab,
  setMediaTab,
  mediaFiles,
  setMediaFiles,
  urlMediaItems,
  setUrlMediaItems,
  linkPreview,
  setLinkPreview,
  selectedPlatforms,
  platforms,
  onEditSavedMedia,
}) => {
  const { t } = useTranslation();

  const savedCount =
    mediaFiles.length + urlMediaItems.filter((item) => item.status === 'done').length;

  const TABS: {
    id: MediaTab;
    icon: React.FC<{ className?: string }>;
    label: string;
    tooltip: string;
    badge?: number;
  }[] = [
    {
      id: 'saved',
      icon: Paperclip,
      label: t('compose_media_saved', 'Saved'),
      tooltip: t('compose_media_saved_tooltip', 'Media currently attached to this post'),
      badge: savedCount,
    },
    { id: 'library', icon: FolderOpen, label: t('compose_media_library', 'Library'), tooltip: t('compose_media_library_tooltip', 'Browse your media library') },
    { id: 'upload', icon: Upload, label: t('compose_media_upload', 'Upload'), tooltip: t('compose_media_upload_tooltip', 'Upload files from your device') },
    { id: 'url', icon: Link2, label: t('compose_media_url', 'URL'), tooltip: t('compose_media_url_tooltip', 'Import media from a direct URL') },
    { id: 'link', icon: Globe, label: t('compose_media_link', 'Link'), tooltip: t('compose_media_link_tooltip', 'Attach a link with OG preview card') },
    { id: 'cloud', icon: HardDrive, label: t('compose_media_cloud', 'Cloud'), tooltip: t('compose_media_cloud_tooltip', 'Import from Google Drive or Dropbox') },
    { id: 'ai', icon: Sparkles, label: t('compose_media_ai_generate', 'AI Generate'), tooltip: t('compose_media_ai_generate_tooltip', 'Generate images with AI') },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3.5">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">{t('compose_media_heading', 'Media')}</h4>
        <div className="flex rounded-lg bg-gray-100 p-0.5">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = mediaTab === tab.id;
            const showBadge = typeof tab.badge === 'number' && tab.badge > 0;
            return (
              <button
                key={tab.id}
                onClick={() => setMediaTab(tab.id)}
                title={tab.tooltip}
                className={`flex h-7.5 items-center gap-1 px-2 rounded-md text-[11px] font-medium transition-all ${
                  isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-3 w-3" />
                <span className="hidden sm:inline">{tab.label}</span>
                {showBadge && (
                  <span
                    className={`ml-0.5 inline-flex h-4 min-w-[15px] items-center justify-center rounded-full px-1 text-[9px] font-semibold ${
                      isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`overflow-y-auto ${mediaTab === 'ai' ? 'min-h-[180px]' : 'h-[180px]'}`}>
        {mediaTab === 'saved' && (
          <SavedMediaTab
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
            urlMediaItems={urlMediaItems}
            setUrlMediaItems={setUrlMediaItems}
            onEditSavedMedia={onEditSavedMedia}
          />
        )}
        {mediaTab === 'library' && (
          <MediaLibraryTab setUrlMediaItems={setUrlMediaItems} />
        )}
        {mediaTab === 'upload' && (
          <MediaUploadTab mediaFiles={mediaFiles} setMediaFiles={setMediaFiles} />
        )}
        {mediaTab === 'url' && (
          <UrlImportTab urlMediaItems={urlMediaItems} setUrlMediaItems={setUrlMediaItems} />
        )}
        {mediaTab === 'link' && (
          <LinkAttachmentTab
            linkPreview={linkPreview}
            setLinkPreview={setLinkPreview}
            selectedPlatforms={selectedPlatforms}
            platforms={platforms}
          />
        )}
        {mediaTab === 'cloud' && (
          <CloudStorageTab setUrlMediaItems={setUrlMediaItems} />
        )}
        {mediaTab === 'ai' && (
          <AIGenerateTab setUrlMediaItems={setUrlMediaItems} />
        )}
      </div>
    </div>
  );
};
